<template>
  <v-hover>
    <template #default="{ isHovering, props }">
      <v-list-item
        class="box-item-list overflow-x-hidden"
        :data-test-id="boxTestId"
        density="compact"
        exact
        rounded
        slim
        variant="text"
        v-bind="mergeProps(props, $attrs)"
        @blur="onBlur"
        @focus="onFocus"
      >
        <!--
          Dynamically inherit slots from parent
          & make all slots from component reusable from parent
        -->
        <template v-for="(_, slotName) in $slots" #[slotName]="slotData">
          <slot :name="slotName" v-bind="slotData" />
        </template>

        <template #prepend>
          <div class="mr-2">
            <BoxAvatar
              :box="box as BoxEntry"
              :box-size="boxAvatarBoxSize"
              :is-inbox-box="isInbox"
              :is-shared-box="isShared"
              :size="boxAvatarSize"
            />
          </div>
        </template>
        <template #title>
          <BoxItemTitle
            :box="box as BoxEntry"
            :box-avatar-initials-size="boxAvatarInitialsSize"
            :box-avatar-size="boxAvatarSize"
            :class="{
              'flex-grow-1': !isDeadline,
              'flex-column': !isDeadline,
            }"
            :is-box-selection-menu="isBoxSelectionMenu"
            :is-deadline="isDeadline"
            :is-favourite="isFavourite"
            :is-inbox="isInbox"
            :is-recent="isRecent"
            :is-shared="isShared"
            :show-box-name-subtitle="showBoxNameSubtitle"
          />
        </template>

        <template
          v-if="$slots.append || showFavorite || showSelected"
          #append="props"
        >
          <slot
            :is-list-item-focused="isFocused"
            :is-list-item-hovering="isHovering"
            v-bind="props"
            name="append"
          >
            <VDefaultsProvider
              :defaults="{
                VIcon: {
                  size: '12',
                },
              }"
            >
              <BoxFavoritesBtn
                v-if="showFavorite"
                :box="box as BoxEntry"
                :class="{
                  'favourite-icon-inactive': !isFocused || props.isActive,
                }"
                density="comfortable"
                :is-focused="isFocused"
                :tabindex="isFocused ? 0 : -1"
                @blur="onBlur"
                @focus="onFocus"
              />

              <v-icon
                v-if="showSelected"
                color="primary"
                :icon="isSelected ? 'fas fa-check' : undefined"
              />
            </VDefaultsProvider>
          </slot>
        </template>
      </v-list-item>
    </template>
  </v-hover>
</template>

<script lang="ts" setup>
import { mergeProps } from 'vue'
import { useFocus } from '@deepcloud/deep-ui-utils/vue'
import BoxAvatar from '@/components/box/BoxAvatar.vue'
import BoxFavoritesBtn from '@/components/box/BoxFavoritesBtn.vue'
import BoxItemTitle from '@/components/box/BoxItemTitle.vue'
import type { Box, BoxEntry, BoxInfo } from '@deepcloud/api-deep-box'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  isShared: {
    type: Boolean,
    default: false,
  },
  isInbox: {
    type: Boolean,
    default: false,
  },
  isRecent: {
    type: Boolean,
    default: false,
  },
  isFavourite: {
    type: Boolean,
    default: false,
  },
  isDeadline: {
    type: Boolean,
    default: false,
  },
  box: {
    type: Object as PropType<BoxInfo | Box | BoxEntry>,
    default: undefined,
  },
  boxAvatarSize: {
    type: Number,
    default: undefined,
  },
  boxAvatarBoxSize: {
    type: Number,
    default: undefined,
  },
  boxAvatarInitialsSize: {
    type: String,
    default: undefined,
  },
  isBoxSelectionMenu: {
    type: Boolean,
    default: false,
  },
  showBoxNameSubtitle: {
    type: Boolean,
    default: false,
  },
  showFavorite: {
    type: Boolean,
    default: false,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  showSelected: {
    type: Boolean,
    default: false,
  },
})

const { isFocused, onFocus, onBlur } = useFocus()

const boxTestId = computed(() => {
  if (!props.box) return ''

  const name = 'boxName' in props.box ? props.box.boxName : ''

  if (!name) return ''

  if (props.isInbox) {
    return `inbox-item-${name}`
  } else if (props.isDeadline) {
    return `deadline-item-${name}`
  } else if (props.isFavourite) {
    return `favorite-item-${name}`
  } else if (props.isRecent) {
    return `recent-item-${name}`
  }
  return `box-item-${name}`
})
</script>
<style scoped lang="scss">
.box-item-list {
  &:hover {
    .favourite-icon-inactive {
      opacity: 1;
    }
  }

  &:focus {
    .favourite-icon-inactive {
      opacity: 1;
    }
  }
}
</style>
